import { Component, Inject, OnInit } from '@angular/core';
import {
	MatDialogConfig,
	MatDialogRef,
	MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { recalculateMargins } from '@core/util/number';
import { Document } from '@models/matter';
import { DocumentMargins, MarginUnit, PageSize } from '@models/documents';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { BusinessDocumentsState } from '@state/business/documents';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { RadioGroupItem } from '../../../atomic/atoms/radio-group/radio-group.component';

export interface DocumentSettingsDialogData {
	defaultMargins: DocumentMargins;
	defaultPageSize: PageSize;
	headingText?: string;
	hideRestoreDefaults?: boolean;
	margins?: DocumentMargins | null;
	pageSize: PageSize;
	document?: Document;
}

export interface DocumentSettingsSuccessData {
	margins: DocumentMargins;
	pageSize: PageSize;
}

export const defaultDocumentSettingsDialogConfig: MatDialogConfig = {
	width: '400px',
};

@UntilDestroy()
@Component({
	selector: 'app-document-settings',
	templateUrl: 'document-settings.component.html',
	standalone: false,
})
export class DocumentSettingsComponent implements OnInit {
	headingText = 'Page settings';
	hideRestoreDefaults?: boolean;
	margins: DocumentMargins;
	pageSize: PageSize;
	settingsReset = false;
	readonly PageSizeOptions: RadioGroupItem<PageSize>[] = [
		{
			label: 'Letter',
			value: PageSize.LETTER,
		},
		{
			label: 'Legal',
			value: PageSize.LEGAL,
		},
	];
	readonly UnitOptions: RadioGroupItem<MarginUnit>[] = [
		{
			label: MarginUnit.CENTIMETER,
			value: MarginUnit.CENTIMETER,
		},
		{
			label: MarginUnit.INCHES,
			value: MarginUnit.INCHES,
		},
		{
			label: MarginUnit.POINT,
			value: MarginUnit.POINT,
		},
	];

	@Select(BusinessState.getDefaultPageDialogData)
	defaultPageData$: Observable<DocumentSettingsDialogData>;
	@SelectSnapshot(BusinessDocumentsState.getAll) businessDocuments: Document[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(BusinessState.useHtmlDocuments)
	useHtmlDocuments$: Observable<boolean>;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: DocumentSettingsDialogData,
		private dialogRef: MatDialogRef<
			DocumentSettingsComponent,
			DocumentSettingsSuccessData
		>,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.hideRestoreDefaults = !!this.data.hideRestoreDefaults;
		this.margins = recalculateMargins(
			MarginUnit.POINT,
			this.data.margins.unit,
			this.data.margins,
		);
		this.pageSize = this.data.pageSize;
		if (this.data.headingText) {
			this.headingText = this.data.headingText;
		}
	}

	cancel(): void {
		this.dialogRef.close();
	}

	pageSizeChange(newPageSize: PageSize): void {
		this.pageSize = newPageSize;
	}

	reset(): void {
		// If document it's a business document reset to closer default AKA it's editing in the templates page
		const isInDocumentTemplates = this.router.url.includes(
			UrlService.Manage_Business_Template_Documents(),
		);
		if (isInDocumentTemplates) {
			this.settingsReset = true;
			this.defaultPageData$
				.pipe(untilDestroyed(this), first())
				.subscribe(({ defaultMargins, defaultPageSize }) => {
					this.margins = recalculateMargins(
						MarginUnit.POINT,
						defaultMargins.unit,
						defaultMargins,
					);
					this.pageSize = defaultPageSize;
				});
		} else {
			const businessDoc = this.businessDocuments.find(
				(businessDoc) =>
					businessDoc.id === this.data.document.id ||
					businessDoc.id === this.data.document.source,
			);
			this.margins = businessDoc?.margins
				? recalculateMargins(
						MarginUnit.POINT,
						businessDoc?.margins?.unit,
						businessDoc?.margins,
					)
				: recalculateMargins(
						MarginUnit.POINT,
						this.data.defaultMargins.unit,
						this.data.defaultMargins,
					);
			this.pageSize = businessDoc?.pageSize;
		}
	}

	save(): void {
		const CurrentUnit = this.margins.unit;
		this.unitChange(MarginUnit.POINT);
		const margins = this.settingsReset
			? null
			: {
					...this.margins,
					unit: CurrentUnit,
				};
		this.settingsReset = false;

		this.dialogRef.close({
			margins: margins,
			pageSize: this.pageSize,
		});
	}

	unitChange(newUnit: MarginUnit): void {
		this.margins = recalculateMargins(this.margins.unit, newUnit, this.margins);
	}
}
